import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatAmount, getFormatConfig } from '../../../utils/global';
import { filterGlAccountsForBusinessUnit } from './invoiceReviewUtils';
import {
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAccentLinkSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const getGlAccountOptionLabel = (option) => {
  if (!option) return '';
  return option.glAccount || `${option.glCode || ''} - ${option.glDescription || ''}`;
};

const emptySplitRow = (overrides = {}) => ({
  key: `split-${Date.now()}-${Math.random()}`,
  businessUnitId: '',
  glAccountId: '',
  splitAmount: '',
  splitPercent: '',
  ...overrides,
});

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const calcAmountFromPercent = (percent, total) => {
  if (!total) return 0;
  return roundMoney((Number(percent) || 0) * total / 100);
};

const calcPercentFromAmount = (amount, total) => {
  if (!total) return '';
  return String(roundMoney((Number(amount) || 0) * 100 / total));
};

const buildEvenSplitAmounts = (total, count) => {
  const lineCount = Math.max(1, Number(count) || 1);
  if (total <= 0) return Array.from({ length: lineCount }, () => 0);

  const baseAmount = roundMoney(total / lineCount);
  const amounts = [];
  let allocated = 0;

  for (let index = 0; index < lineCount - 1; index += 1) {
    amounts.push(baseAmount);
    allocated += baseAmount;
  }
  amounts.push(roundMoney(total - allocated));
  return amounts;
};

const formatPercentOfOriginal = (row, lineTotal) => {
  if (!lineTotal || row.splitAmount === '' || row.splitAmount == null) return '—';
  const percent = Number(row.splitPercent);
  if (Number.isNaN(percent)) return '—';
  return `${percent}%`;
};

const getSplitRowGridColumns = ({ readOnly, allocationMode }) => {
  const checkboxCol = readOnly ? '' : '40px ';
  if (allocationMode === 'percent') {
    return readOnly
      ? '1fr 1fr 100px minmax(140px, 0.75fr)'
      : `${checkboxCol}1fr 1fr 100px minmax(140px, 0.75fr) 40px`;
  }
  return readOnly
    ? '1fr 1fr minmax(140px, 0.75fr) 100px'
    : `${checkboxCol}1fr 1fr minmax(140px, 0.75fr) 100px 40px`;
};

const InvoiceLineSplitDialog = ({
  open,
  onClose,
  tenantId,
  apInvoiceId,
  line,
  businessUnits = [],
  glAccounts = [],
  readOnly = false,
  onSaved,
  showError,
  showSuccess,
}) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);
  const canModifySplits = canAdd || canEdit;
  const effectiveReadOnly = readOnly || !canModifySplits;
  const config = getFormatConfig();
  const lineTotal = Number(line?.amount) || 0;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [evenSplitCount, setEvenSplitCount] = useState('2');
  const [allocationMode, setAllocationMode] = useState('amount');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const loadSplits = useCallback(async () => {
    if (!open || !tenantId || !line?.apInvoiceLineId) return;
    setLoading(true);
    try {
      const data = await apiFetch(
        `conductor/ap-invoice-line-splits/by-ap-invoice-line?tenant_id=${tenantId}&ap_invoice_line_id=${line.apInvoiceLineId}`
      );
      const list = Array.isArray(data) ? data : [];
      if (list.length) {
        setRows(list.map((split) => {
          const splitAmount = split.splitAmount != null ? String(split.splitAmount) : '';
          return {
            key: split.apInvoiceLineSplitId || `split-${split.splitSequence}`,
            businessUnitId: split.businessUnitId || '',
            glAccountId: split.glAccountId || '',
            splitAmount,
            splitPercent: calcPercentFromAmount(splitAmount, lineTotal),
          };
        }));
      } else if (effectiveReadOnly) {
        setRows([]);
      } else {
        setRows([{
          key: 'split-1',
          businessUnitId: line.businessUnitId || '',
          glAccountId: line.glAccountId || '',
          splitAmount: lineTotal ? String(lineTotal) : '',
          splitPercent: lineTotal ? '100' : '',
        }]);
      }
    } catch (err) {
      showError?.(err.message || 'Failed to load line splits');
      setRows(effectiveReadOnly ? [] : [emptySplitRow()]);
    } finally {
      setLoading(false);
    }
  }, [open, tenantId, line, lineTotal, effectiveReadOnly, showError]);

  useEffect(() => {
    if (open) {
      setAllocationMode('amount');
      setEvenSplitCount('2');
      setSelectedRowKeys([]);
      loadSplits();
    }
  }, [open, loadSplits]);

  const splitSum = useMemo(
    () => rows.reduce((sum, row) => sum + (Number(row.splitAmount) || 0), 0),
    [rows]
  );

  const percentSum = useMemo(
    () => roundMoney(rows.reduce((sum, row) => sum + (Number(row.splitPercent) || 0), 0)),
    [rows]
  );

  const remaining = lineTotal - splitSum;
  const isBalanced = lineTotal <= 0 || Math.abs(remaining) < 0.01;
  const isPercentBalanced = Math.abs(percentSum - 100) < 0.01;
  const isOverAllocated = lineTotal > 0 && remaining < -0.009;
  const isUnderAllocated = lineTotal > 0 && remaining > 0.009;
  const hasAllocationError = isOverAllocated || isUnderAllocated;

  const splitValidationMessage = useMemo(() => {
    if (!lineTotal || isBalanced) {
      if (allocationMode === 'percent' && lineTotal > 0 && !isPercentBalanced && percentSum > 100.009) {
        return `Total percentage allocation exceeds 100% (${percentSum.toFixed(2)}%).`;
      }
      return '';
    }
    if (isOverAllocated) {
      return `Allocated split amount exceeds the original invoice line total by ${formatAmount(Math.abs(remaining), config.AmountFormat)}.`;
    }
    if (isUnderAllocated) {
      return `Allocated split amount is ${formatAmount(remaining, config.AmountFormat)} below the original invoice line total.`;
    }
    return '';
  }, [
    lineTotal,
    isBalanced,
    isOverAllocated,
    isUnderAllocated,
    remaining,
    allocationMode,
    isPercentBalanced,
    percentSum,
    config.AmountFormat,
  ]);

  const updateRow = (key, field, value) => {
    setRows((prev) => prev.map((row) => {
      if (row.key !== key) return row;

      if (field === 'splitPercent') {
        const splitAmount = calcAmountFromPercent(value, lineTotal);
        return {
          ...row,
          splitPercent: value,
          splitAmount: lineTotal > 0 ? String(splitAmount) : row.splitAmount,
        };
      }

      if (field === 'splitAmount') {
        return {
          ...row,
          splitAmount: value,
          splitPercent: calcPercentFromAmount(value, lineTotal),
        };
      }

      return { ...row, [field]: value };
    }));
  };

  const addRow = () => {
    if (!canAdd && !canEdit) return;
    setRows((prev) => [...prev, emptySplitRow({
      businessUnitId: line?.businessUnitId || prev[0]?.businessUnitId || '',
      glAccountId: line?.glAccountId || prev[0]?.glAccountId || '',
    })]);
  };

  const applyEvenSplit = () => {
    const amounts = buildEvenSplitAmounts(lineTotal, evenSplitCount);
    const template = rows[0] || {
      businessUnitId: line?.businessUnitId || '',
      glAccountId: line?.glAccountId || '',
    };

    setRows(amounts.map((amount, index) => emptySplitRow({
      key: `split-even-${index}-${Date.now()}`,
      businessUnitId: rows[index]?.businessUnitId || template.businessUnitId,
      glAccountId: rows[index]?.glAccountId || template.glAccountId,
      splitAmount: String(amount),
      splitPercent: lineTotal > 0 ? calcPercentFromAmount(amount, lineTotal) : '',
    })));
    setSelectedRowKeys([]);
    setAllocationMode('amount');
  };

  const handleAllocationModeChange = (_, nextMode) => {
    if (!nextMode) return;
    setAllocationMode(nextMode);
    if (nextMode === 'percent') {
      setRows((prev) => prev.map((row) => ({
        ...row,
        splitPercent: row.splitPercent || calcPercentFromAmount(row.splitAmount, lineTotal),
        splitAmount: String(calcAmountFromPercent(row.splitPercent || calcPercentFromAmount(row.splitAmount, lineTotal), lineTotal)),
      })));
    }
  };

  const removeRow = (key) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((row) => row.key !== key)));
    setSelectedRowKeys((prev) => prev.filter((item) => item !== key));
  };

  const toggleRowSelected = (key) => {
    setSelectedRowKeys((prev) => (
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    ));
  };

  const allRowKeys = rows.map((row) => row.key);
  const allSelected = allRowKeys.length > 0 && allRowKeys.every((key) => selectedRowKeys.includes(key));
  const someSelected = selectedRowKeys.length > 0 && !allSelected;

  const selectedRowsGlAccountId = useMemo(() => {
    if (!selectedRowKeys.length) return '';
    const glIds = [...new Set(
      rows
        .filter((row) => selectedRowKeys.includes(row.key))
        .map((row) => row.glAccountId || '')
    )];
    return glIds.length === 1 ? glIds[0] : '';
  }, [selectedRowKeys, rows]);

  const applyGlToSelectedRows = (glAccountId) => {
    if (!selectedRowKeys.length) return;
    const selected = new Set(selectedRowKeys);
    setRows((prev) => prev.map((row) => (
      selected.has(row.key) ? { ...row, glAccountId: glAccountId || '' } : row
    )));
  };

  const handleSave = async () => {
    if (!canModifySplits) return;
    if (!tenantId || !apInvoiceId || !line?.apInvoiceLineId) return;
    if (!isBalanced) {
      showError?.(splitValidationMessage || 'Split amounts must equal the line total.', 'Invalid Split');
      return;
    }
    if (allocationMode === 'percent' && !isPercentBalanced) {
      showError?.('Split percentages must total 100%.', 'Invalid Split');
      return;
    }
    if (rows.some((row) => !row.businessUnitId || !row.glAccountId || !(Number(row.splitAmount) > 0))) {
      showError?.('Each split row requires a business unit, GL account, and amount.', 'Invalid Split');
      return;
    }

    setSaving(true);
    try {
      const saved = await apiFetch('conductor/ap-invoice-line-splits/by-ap-invoice-line', {
        method: 'PUT',
        body: JSON.stringify({
          tenantId,
          apInvoiceId,
          apInvoiceLineId: line.apInvoiceLineId,
          updatedBy: getUserId(),
          splits: rows.map((row) => ({
            businessUnitId: row.businessUnitId,
            glAccountId: row.glAccountId,
            splitAmount: Number(row.splitAmount),
          })),
        }),
      });
      showSuccess?.('Line split saved successfully');
      onSaved?.(saved);
      onClose();
    } catch (err) {
      showError?.(err.message || 'Failed to save line split');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
      <DialogTitle sx={harmonateDialogTitleSx}>{effectiveReadOnly ? 'View Line Split' : 'Split Line Amount'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {line?.description || 'Line item'}
          </Typography>
          <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
            Line total: {formatAmount(lineTotal, config.AmountFormat)}
            {!effectiveReadOnly ? (
              <>
                {' · '}
                Allocated: {formatAmount(splitSum, config.AmountFormat)}
                {' · '}
                <Box
                  component="span"
                  sx={{
                    color: isOverAllocated ? 'error.main' : isUnderAllocated ? 'warning.main' : 'inherit',
                    fontWeight: hasAllocationError ? 600 : 'inherit',
                  }}
                >
                  Remaining: {formatAmount(remaining, config.AmountFormat)}
                </Box>
                {rows.length ? ` · Total %: ${percentSum.toFixed(2)}%` : ''}
              </>
            ) : null}
          </Typography>
        </Box>

        {!effectiveReadOnly ? (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              alignItems: 'center',
              p: 1.5,
              borderRadius: 1,
              bgcolor: ht.listBg,
              border: `1px solid ${ht.line}`,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: ht.text }}>
              Auto Split
            </Typography>
            <TextField
              size="small"
              label="Split lines"
              type="number"
              value={evenSplitCount}
              onChange={(e) => setEvenSplitCount(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 1, step: 1 }}
              sx={{ width: 110 }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={applyEvenSplit}
              disabled={loading || saving || lineTotal <= 0}
              sx={{ textTransform: 'none', borderColor: ht.darkBlue, color: ht.text }}
            >
              Even Split
            </Button>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={allocationMode}
              onChange={handleAllocationModeChange}
              sx={{ ml: { sm: 'auto' } }}
            >
              <ToggleButton value="amount" sx={{ textTransform: 'none' }}>Amount</ToggleButton>
              <ToggleButton value="percent" sx={{ textTransform: 'none' }}>% Allocation</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        ) : null}

        {!effectiveReadOnly && rows.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => setSelectedRowKeys(allSelected ? [] : allRowKeys)}
              inputProps={{ 'aria-label': 'Select all split rows' }}
              sx={{ p: 0.5 }}
            />
            <Typography variant="body2" color="text.secondary">
              {selectedRowKeys.length ? `${selectedRowKeys.length} selected` : 'Select rows'}
            </Typography>
            <Autocomplete
              size="small"
              disabled={!selectedRowKeys.length}
              options={filterGlAccountsForBusinessUnit(
                glAccounts,
                businessUnits,
                line?.businessUnitId,
                selectedRowsGlAccountId
              )}
              getOptionLabel={getGlAccountOptionLabel}
              value={
                filterGlAccountsForBusinessUnit(glAccounts, businessUnits, line?.businessUnitId)
                  .find((account) => String(account.ledgerAccountId) === String(selectedRowsGlAccountId))
                || glAccounts.find((account) => String(account.ledgerAccountId) === String(selectedRowsGlAccountId))
                || null
              }
              onChange={(_, value) => applyGlToSelectedRows(value?.ledgerAccountId || '')}
              isOptionEqualToValue={(a, b) => String(a.ledgerAccountId) === String(b?.ledgerAccountId)}
              sx={{ minWidth: 280, flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Apply GL to selected"
                  placeholder="Search GL account"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Box>
        ) : null}

        {!effectiveReadOnly && splitValidationMessage ? (
          <Alert severity="error" sx={{ py: 0.25 }}>
            {splitValidationMessage}
          </Alert>
        ) : null}

        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading splits...</Typography>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No split rows found for this line.</Typography>
        ) : (
          rows.map((row, index) => (
            <Box
              key={row.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: getSplitRowGridColumns({ readOnly: effectiveReadOnly, allocationMode }),
                gap: 1.5,
                alignItems: 'flex-start',
              }}
            >
              {!effectiveReadOnly ? (
                <Checkbox
                  size="small"
                  checked={selectedRowKeys.includes(row.key)}
                  onChange={() => toggleRowSelected(row.key)}
                  inputProps={{ 'aria-label': `Select split row ${index + 1}` }}
                  sx={{ mt: 1, p: 0.5 }}
                />
              ) : null}
              <Autocomplete
                fullWidth
                size="small"
                disabled={effectiveReadOnly}
                options={businessUnits}
                getOptionLabel={(option) => option.businessUnitName || option.label || ''}
                value={businessUnits.find((bu) => String(bu.businessUnitId) === String(row.businessUnitId)) || null}
                onChange={(_, value) => {
                  const nextBuId = value?.businessUnitId || '';
                  const nextGlOptions = filterGlAccountsForBusinessUnit(glAccounts, businessUnits, nextBuId);
                  const glStillValid = nextGlOptions.some(
                    (account) => String(account.ledgerAccountId) === String(row.glAccountId)
                  );
                  setRows((prev) => prev.map((item) => (
                    item.key === row.key
                      ? {
                          ...item,
                          businessUnitId: nextBuId,
                          glAccountId: glStillValid ? item.glAccountId : '',
                        }
                      : item
                  )));
                }}
                isOptionEqualToValue={(a, b) => String(a.businessUnitId) === String(b?.businessUnitId)}
                renderInput={(params) => (
                  <TextField {...params} label={`Business Unit ${index + 1}`} InputLabelProps={{ shrink: true }} />
                )}
              />
              <Autocomplete
                fullWidth
                size="small"
                disabled={effectiveReadOnly}
                options={filterGlAccountsForBusinessUnit(
                  glAccounts,
                  businessUnits,
                  row.businessUnitId,
                  row.glAccountId
                )}
                getOptionLabel={getGlAccountOptionLabel}
                value={
                  filterGlAccountsForBusinessUnit(glAccounts, businessUnits, row.businessUnitId)
                    .find((account) => String(account.ledgerAccountId) === String(row.glAccountId))
                  || glAccounts.find((account) => String(account.ledgerAccountId) === String(row.glAccountId))
                  || null
                }
                onChange={(_, value) => updateRow(row.key, 'glAccountId', value?.ledgerAccountId || '')}
                isOptionEqualToValue={(a, b) => String(a.ledgerAccountId) === String(b?.ledgerAccountId)}
                renderInput={(params) => (
                  <TextField {...params} label={`GL Account ${index + 1}`} InputLabelProps={{ shrink: true }} />
                )}
              />
              {(allocationMode === 'percent') ? (
                <TextField
                  fullWidth
                  size="small"
                  label="% Allocation"
                  type="number"
                  disabled={effectiveReadOnly}
                  value={row.splitPercent}
                  onChange={(e) => updateRow(row.key, 'splitPercent', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, max: 100, step: '0.01' }}
                  error={hasAllocationError || (allocationMode === 'percent' && !isPercentBalanced)}
                />
              ) : null}
              <TextField
                fullWidth
                size="small"
                label="Amount"
                type="number"
                disabled={effectiveReadOnly || allocationMode === 'percent'}
                value={row.splitAmount}
                onChange={(e) => updateRow(row.key, 'splitAmount', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, step: '0.01' }}
                error={hasAllocationError}
              />
              {(allocationMode === 'amount' || effectiveReadOnly) ? (
                <TextField
                  fullWidth
                  size="small"
                  label="% of Original"
                  value={formatPercentOfOriginal(row, lineTotal)}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  error={hasAllocationError}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: hasAllocationError ? 'error.main' : 'text.secondary',
                      fontWeight: 500,
                    },
                  }}
                />
              ) : null}
              {!effectiveReadOnly ? (
                <IconButton
                  size="small"
                  onClick={() => removeRow(row.key)}
                  disabled={rows.length <= 1 || !canDelete}
                  aria-label="Remove split row"
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          ))
        )}

        {!effectiveReadOnly ? (
          <Button
            startIcon={<AddIcon />}
            onClick={addRow}
            disabled={loading || saving || !canAdd}
            sx={{ alignSelf: 'flex-start', ...harmonateAccentLinkSx }}
          >
            Add split row
          </Button>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} variant="outlined" sx={harmonateCancelButtonSx}>{effectiveReadOnly ? 'Close' : 'Cancel'}</Button>
        {!effectiveReadOnly ? (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving || !isBalanced || Boolean(splitValidationMessage)}
            sx={harmonateProceedButtonSx}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceLineSplitDialog;
